import { Channel } from "types";
import permissions from "./channelPermissions";
import { useEffect, useState } from "react";
import Icon from "@/icons";

export default function UpdatePermissions({
    channel,
}: {
    channel: Channel;
}) {
    const req = async () => {
        const roles = await fetch(`__SITE_BASE__/api/teams/${channel.teamId}/roles`);
        return (await roles.json()).roles as any[];
    };
    const [channelData, setChannelData] = useState<any>(channel);
    const [roles, setRoles] = useState<any[]>([]);
    const [selectedRole, setSelectedRole] = useState<any>(null);

    useEffect(() => {
        req().then(setRoles);
    }, []);

    useEffect(() => {
        setSelectedRole(roles[0]);
    }, [roles]);

    const updateRole = async (roleId: string, allow: bigint, deny: bigint) => {
        await fetch(`__SITE_BASE__/api/teams/${channel.teamId}/channels/${channel.id}/permissions/role/${roleId}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                allow: Number(allow),
                deny: Number(deny),
            }),
        });
    };

    const togglePermission = (permission: bigint, type: 'allow' | 'deny' | 'default') => {
        if (!selectedRole) return;
    
        const currentPermissions = channelData.permissions?.role?.[selectedRole.id] || { allow: 0n, deny: 0n };
        const newPermissions = { ...currentPermissions };
    
        if (type === 'allow') {
            if (!(BigInt(currentPermissions.allow) & BigInt(permission))) {
                newPermissions.allow = BigInt(currentPermissions.allow) | BigInt(permission);
                newPermissions.deny = BigInt(currentPermissions.deny) & ~BigInt(permission);
            } else {
                newPermissions.allow = BigInt(currentPermissions.allow) & ~BigInt(permission);
            }
        }
    
        if (type === 'deny') {
            if (!(BigInt(currentPermissions.deny) & BigInt(permission))) {
                newPermissions.deny = BigInt(currentPermissions.deny) | BigInt(permission);
                newPermissions.allow = BigInt(currentPermissions.allow) & ~BigInt(permission);
            } else {
                newPermissions.deny = BigInt(currentPermissions.deny) & ~BigInt(permission);
            }
        }
    
        if(type === 'default') {
            newPermissions.allow = BigInt(currentPermissions.allow) & ~BigInt(permission);
            newPermissions.deny = BigInt(currentPermissions.deny) & ~BigInt(permission);
        }

        setChannelData((prevChannelData: any) => ({
            ...prevChannelData,
            permissions: {
                ...prevChannelData.permissions,
                role: {
                    ...prevChannelData.permissions?.role,
                    [selectedRole.id]: newPermissions,
                },
            },
        }));
    };
    
    return (
        <>
            <div className="sm:flex sm:flex-col w-full h-full mb-12">
                <div className="sm:flex sm:flex-col w-full">
                    <div className="sm:flex items-start min-w-full">
                        <div className="flex items-center">
                            <div className="flex flex-col items-start w-full">
                                <div className="flex items-center w-full">
                                    <span className="text-lg font-semibold text-gray-200">Roles</span>
                                </div>
                                <div className="flex flex-col w-full"></div>
                                    <div className="mt-4 overflow-y-auto max-h-[16rem] sm:max-h-[45rem] w-full sm:min-w-[16rem]" style={{ scrollbarWidth: "thin", scrollbarColor: "rgb(40, 160, 42) rgb(39 42 47)" }}>
                                        {roles.map((role) => (
                                            <div key={role.id} className={`flex items-center justify-between w-full p-2 rounded-lg mb-2 cursor-pointer ${selectedRole?.id == role.id ? "bg-brand-dark hover:bg-brand-darkest" : "hover:bg-brand-darkest/50"}`} onClick={() => setSelectedRole(role)}>
                                                <div className="flex items-center">
                                                    <div className="w-4 h-4 rounded-full" style={{ background: role.color2 ? `linear-gradient(90deg, ${role.color}, ${role.color2})` : role.color }}></div>
                                                    <span className="ml-2 text-white">{role.name.length > 20 ? role.name.slice(0, 20) + "..." : role.name}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        {selectedRole?.id && (
                            <div className="flex flex-col items-start sm:ml-8 sm:w-3/4 max-h-[55rem] overflow-y-auto sm:p-5" style={{ scrollbarWidth: "thin", scrollbarColor: "rgb(40, 160, 42) rgb(39 42 47)" }}>
                                    {channel.type == "voice" && (
                                        <div className="mb-4 w-full">
                                        <div className="flex items-center w-full bg-red-400/50 rounded-lg border py-5 border-red-500/40">
                                            <div className="w-16 text-center text-white items-center justify-center flex">
                                                <div className="border-2 rounded-full px-2 py-2 border-gray-300/50">
                                                    <Icon name="x" size="24" />
                                                </div>
                                            </div>
                                            <div className="flex flex-col items-start">
                                                <span className="text-white text-sm font-semibold">Voice Channels are not supported yet</span>
                                                <span className="text-gray-300 text-sm">Voice channels do not support permissions yet. This feature is coming soon.</span>
                                            </div>
                                        </div>
                                    </div>
                                        )}
                                <div className="flex items-center justify-between w-full">
                                    <span className="text-lg font-semibold text-gray-200">Permissions for {selectedRole.name}</span>
                                    <div className="flex">
                                        <button
                                            onClick={() => {
                                                updateRole(selectedRole.id, BigInt(channelData.permissions?.role?.[selectedRole.id]?.allow || 0n), BigInt(channelData.permissions?.role?.[selectedRole.id]?.deny || 0n));
                                            }}
                                            className="text-gray-200 flex gap-2 items-center bg-brand-darkest text-left px-2 rounded-lg py-2 hover:text-white"
                                        >
                                            Save
                                        </button>
                                    </div>
                                </div>
                                <div className="flex flex-col w-full mt-4">
                                    {Object.entries(permissions[channel.type as keyof typeof permissions]).map(([key, value]) => (
                                        <div key={key} className="flex items-center justify-between w-full p-2 rounded-lg mb-2">
                                            <div className="flex flex-col items-start">
                                                <span className="text-white">{value.title}</span>
                                                <span className="text-gray-400">{value.description}</span>
                                            </div>
                                            <div className="flex items-center">
                                                <div className="bg-brand-darkest flex rounded-r-lg rounded-l-lg">
                                                    <button 
                                                        className={`rounded-l-lg px-2 py-2 text-white ${channelData.permissions?.role?.[selectedRole.id] ? (BigInt(channelData.permissions?.role?.[selectedRole.id]?.allow) & BigInt(value.value) ? "bg-green-400" : "bg-brand-darkest") : "bg-brand-darkest"}`}
                                                        onClick={() => togglePermission(value.value, 'allow')}
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="h-4 w-4"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M5 13l4 4L19 7"
                                                            />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        className={`px-2 py-2 text-white ${channelData.permissions?.role?.[selectedRole.id] ? (!(BigInt(channelData.permissions?.role?.[selectedRole.id]?.deny) & BigInt(value.value)) && !(BigInt(channelData.permissions?.role?.[selectedRole.id]?.allow) & BigInt(value.value)) ? "bg-brand-green" : "bg-brand-darkest") : "bg-brand-green"}`}
                                                        onClick={() => togglePermission(value.value, 'default')}
                                                    >
                                                        <div className="w-4 h-4 rounded-lg border-2 border-white"/>
                                                    </button>
                                                    <button
                                                        className={`rounded-r-lg px-2 py-2 text-white ${channelData.permissions?.role?.[selectedRole.id] ? (BigInt(channelData.permissions?.role?.[selectedRole.id]?.deny) & BigInt(value.value) ? "bg-red-400" : "bg-brand-darkest") : "bg-brand-darkest"}`}
                                                        onClick={() => togglePermission(value.value, 'deny')}
                                                    >
                                                        <Icon name="x" size="16" color="white" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
